import { resolveApiBaseUrl } from "./apiBase";

const API_URL = resolveApiBaseUrl();

const getAuthToken = (): string | null => {
  const token = localStorage.getItem("idToken") || localStorage.getItem("authToken");
  return token ? token.trim() : null;
};

const authHeaders = (): HeadersInit => {
  const token = getAuthToken();
  const h: HeadersInit = { Accept: "application/json" };
  if (token) {
    (h as Record<string, string>).Authorization = `Bearer ${token}`;
  }
  return h;
};

export type AdminMongoCollectionInfo = {
  name: string;
  count: number;
  /** ISO 8601 max per-doc time (_updatedAt, updatedAt, _createdAt, createdAt, updated, at), or null */
  maxUpdatedAt?: string | null;
};

export type AdminMongoCollectionsResponse = {
  success: boolean;
  database?: string | null;
  collections?: AdminMongoCollectionInfo[];
  message?: string;
};

function normalizeCollectionList(raw: unknown): AdminMongoCollectionInfo[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((item): AdminMongoCollectionInfo => {
    if (typeof item === "string") {
      return { name: item, count: 0 };
    }
    if (item !== null && typeof item === "object" && "name" in item) {
      const o = item as Record<string, unknown>;
      const name = typeof o.name === "string" ? o.name : "";
      const count = typeof o.count === "number" && Number.isFinite(o.count) ? o.count : 0;
      let maxUpdatedAt: string | null | undefined;
      if (o.maxUpdatedAt === null || o.maxUpdatedAt === undefined) {
        maxUpdatedAt = o.maxUpdatedAt === null ? null : undefined;
      } else if (typeof o.maxUpdatedAt === "string") {
        maxUpdatedAt = o.maxUpdatedAt;
      } else {
        maxUpdatedAt = null;
      }
      return { name, count, maxUpdatedAt };
    }
    return { name: String(item), count: 0 };
  });
}

export type AdminMongoDocumentsResponse = {
  success: boolean;
  collection?: string;
  total?: number;
  skip?: number;
  limit?: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  documents?: unknown[];
  message?: string;
};

export async function fetchAdminMongoCollections(): Promise<AdminMongoCollectionsResponse> {
  const res = await fetch(`${API_URL}/auth/admin/mongo/collections`, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    headers: authHeaders(),
  });
  const data = (await res.json().catch(() => ({}))) as AdminMongoCollectionsResponse;
  if (!res.ok) {
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return {
    ...data,
    collections: normalizeCollectionList(data.collections),
  };
}

export async function fetchAdminMongoDocuments(
  collectionName: string,
  opts: {
    limit?: number;
    skip?: number;
    filterJson?: string;
    sortField?: string;
    sortOrder?: "asc" | "desc";
  }
): Promise<AdminMongoDocumentsResponse> {
  const limit = opts.limit ?? 50;
  const skip = opts.skip ?? 0;
  const q = new URLSearchParams();
  q.set("limit", String(limit));
  q.set("skip", String(skip));
  q.set("filter", (opts.filterJson != null ? opts.filterJson.trim() : "") || "{}");
  const sortField = opts.sortField?.trim() || "_id";
  q.set("sortField", sortField);
  q.set("sortOrder", opts.sortOrder ?? "desc");
  const encoded = encodeURIComponent(collectionName);
  const res = await fetch(`${API_URL}/auth/admin/mongo/collections/${encoded}?${q.toString()}`, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    headers: authHeaders(),
  });
  const data = (await res.json().catch(() => ({}))) as AdminMongoDocumentsResponse;
  if (!res.ok) {
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data;
}

export type AdminMongoRenameResponse = {
  success: boolean;
  oldName?: string;
  newName?: string;
  message?: string;
};

export class AdminMongoRestoreConflictError extends Error {
  readonly existingCollection: string;

  constructor(message: string, existingCollection: string) {
    super(message);
    this.name = "AdminMongoRestoreConflictError";
    this.existingCollection = existingCollection;
  }
}

const jsonPostHeaders = (): HeadersInit => {
  const base = authHeaders() as Record<string, string>;
  return { ...base, Accept: "application/json", "Content-Type": "application/json" };
};

export async function postAdminMongoArchiveCollection(
  collectionName: string
): Promise<AdminMongoRenameResponse> {
  const encoded = encodeURIComponent(collectionName);
  const res = await fetch(`${API_URL}/auth/admin/mongo/collections/${encoded}/archive`, {
    method: "POST",
    credentials: "include",
    headers: jsonPostHeaders(),
    body: "{}",
  });
  const data = (await res.json().catch(() => ({}))) as AdminMongoRenameResponse;
  if (!res.ok) {
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data;
}

export async function postAdminMongoRestoreCollection(
  collectionName: string,
  opts?: { archiveExistingFirst?: boolean }
): Promise<AdminMongoRenameResponse> {
  const encoded = encodeURIComponent(collectionName);
  const res = await fetch(`${API_URL}/auth/admin/mongo/collections/${encoded}/restore`, {
    method: "POST",
    credentials: "include",
    headers: jsonPostHeaders(),
    body: JSON.stringify({
      archiveExistingFirst: opts?.archiveExistingFirst === true,
    }),
  });
  const data = (await res.json().catch(() => ({}))) as AdminMongoRenameResponse & {
    conflict?: boolean;
    existingCollection?: string;
  };
  if (res.status === 409 && data.conflict) {
    throw new AdminMongoRestoreConflictError(
      data.message || "Active collection with this name already exists",
      typeof data.existingCollection === "string" ? data.existingCollection : ""
    );
  }
  if (!res.ok) {
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data;
}

export async function postAdminMongoRenameCollection(
  collectionName: string,
  opts: { newName: string }
): Promise<AdminMongoRenameResponse> {
  const encoded = encodeURIComponent(collectionName);
  const res = await fetch(`${API_URL}/auth/admin/mongo/collections/${encoded}/rename`, {
    method: "POST",
    credentials: "include",
    headers: jsonPostHeaders(),
    body: JSON.stringify({ newName: opts.newName.trim() }),
  });
  const data = (await res.json().catch(() => ({}))) as AdminMongoRenameResponse;
  if (!res.ok) {
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data;
}

export async function postAdminMongoDuplicateCollection(
  collectionName: string,
  opts?: { targetName?: string }
): Promise<AdminMongoRenameResponse> {
  const encoded = encodeURIComponent(collectionName);
  const body: Record<string, string | undefined> = {};
  if (opts?.targetName != null && opts.targetName.trim() !== "") {
    body.targetName = opts.targetName.trim();
  }
  const res = await fetch(`${API_URL}/auth/admin/mongo/collections/${encoded}/duplicate`, {
    method: "POST",
    credentials: "include",
    headers: jsonPostHeaders(),
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as AdminMongoRenameResponse;
  if (!res.ok) {
    throw new Error(data.message || `HTTP ${res.status}`);
  }
  return data;
}
